import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { collection, query, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { FileClock, Search, Filter, ShieldCheck, Eye } from 'lucide-react';
import type { AuditLogEntry } from '../../../../../shared/types';

export const AuditLogViewer: React.FC = () => {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('ALL');
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'auditLog'), orderBy('createdAt', 'desc'), limit(150));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: AuditLogEntry[] = [];
        snap.forEach((doc) => list.push(doc.data() as AuditLogEntry));
        setLogs(list);
        setLoading(false);
      },
      (err) => {
        console.warn('[FHPF] Audit logs read restricted or offline:', err.message);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      searchTerm === '' ||
      log.actor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.target?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.action?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesAction = selectedAction === 'ALL' || log.action === selectedAction;
    return matchesSearch && matchesAction;
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileClock className="w-5 h-5 text-indigo-500" />
          <span>Immutable Administrative Audit Trail</span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Append-only cryptographic action log. Every approval, expense, member status change, and bank reconciliation is permanently recorded with actor UID and timestamp.
        </p>
      </div>

      {/* Filter Controls */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search actor, target, action..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-gray-50 dark:bg-navy-950 border border-gray-300 dark:border-navy-700 rounded-xl outline-none text-gray-900 dark:text-white"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Filter className="w-4 h-4 text-gray-400 shrink-0" />
          <select
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value)}
            className="w-full sm:w-auto px-3 py-2 text-xs rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 text-gray-900 dark:text-white outline-none"
          >
            <option value="ALL">All Actions</option>
            <option value="APPROVE_CONTRIBUTION">APPROVE_CONTRIBUTION</option>
            <option value="REJECT_CONTRIBUTION">REJECT_CONTRIBUTION</option>
            <option value="RECORD_EXPENSE">RECORD_EXPENSE</option>
            <option value="REVERSE_EXPENSE">REVERSE_EXPENSE</option>
            <option value="RECORD_LAND_PAYMENT">RECORD_LAND_PAYMENT</option>
            <option value="RECORD_BANK_SNAPSHOT">RECORD_BANK_SNAPSHOT</option>
            <option value="ADD_MEMBER">ADD_MEMBER</option>
            <option value="EXIT_MEMBER">EXIT_MEMBER</option>
            <option value="LEGACY_IMPORT">LEGACY_IMPORT</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : filteredLogs.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400">No matching audit events found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-navy-950/40 uppercase font-semibold text-gray-500">
                <tr>
                  <th className="py-3 px-4">Timestamp (UTC)</th>
                  <th className="py-3 px-4">Actor</th>
                  <th className="py-3 px-4">Action</th>
                  <th className="py-3 px-4">Target Resource</th>
                  <th className="py-3 px-4 text-center">Inspect</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-navy-800">
                {filteredLogs.map((log) => (
                  <tr key={log.auditId} className="hover:bg-gray-50/50 dark:hover:bg-navy-800/40">
                    <td className="py-3 px-4 text-gray-500 whitespace-nowrap font-mono">{log.createdAt}</td>
                    <td className="py-3 px-4">
                      <div className="font-semibold text-gray-900 dark:text-white font-mono">{log.actor}</div>
                      <span className="text-[10px] uppercase font-bold text-gray-400">{log.actorRole}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-0.5 rounded-full font-semibold text-[11px] bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-900">
                        {log.action}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-mono text-gray-700 dark:text-gray-300">{log.target}</td>
                    <td className="py-3 px-4 text-center">
                      <button
                        type="button"
                        onClick={() => setSelectedLog(log)}
                        className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-navy-800 text-gray-600 dark:text-gray-300"
                        title="View payload"
                      >
                        <Eye className="w-4 h-4 text-primary-500" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inspect Detail Modal */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-950/70 backdrop-blur-sm">
          <div className="bg-white dark:bg-navy-900 rounded-2xl max-w-lg w-full p-6 shadow-2xl border border-gray-200 dark:border-navy-800 space-y-3">
            <div className="flex items-center justify-between border-b border-gray-100 dark:border-navy-800 pb-2">
              <span className="font-bold text-sm text-gray-900 dark:text-white">Audit Event Details</span>
              <button
                type="button"
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm font-semibold"
              >
                ✕ Close
              </button>
            </div>

            <div className="space-y-2 text-xs">
              <div>
                <span className="text-gray-400">Audit ID:</span> <span className="font-mono">{selectedLog.auditId}</span>
              </div>
              <div>
                <span className="text-gray-400">Request ID:</span> <span className="font-mono">{selectedLog.requestId}</span>
              </div>
              <div>
                <span className="text-gray-400">State Changes (After):</span>
                <pre className="p-3 mt-1 rounded-xl bg-gray-50 dark:bg-navy-950 border border-gray-200 dark:border-navy-800 font-mono text-[11px] overflow-x-auto text-gray-800 dark:text-gray-200">
                  {JSON.stringify(selectedLog.after || selectedLog.before || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
